import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import axios from "../../axios/AxiosInstance";
import { useNavigate } from "react-router-dom";

export const AddDocumentModal = ({
  isOpen,
  onClose,
  candidate,
  documents,
  setDocuments,
}) => {
  const [formData, setFormData] = useState({
    type: "",
    name: "",
    file: null,
    deliveryDate: "",
    expirationDate: "",
    documentNumber: "",
  });

  const [showIdFields, setShowIdFields] = useState(false);
  const navigate = useNavigate();

  const documentTypes = [
    { id: "Carte d'identité", label: "Carte d'identité" },
    { id: "passport", label: "Passeport" },
    { id: "residence-permit", label: "Titre de séjour" },
    { id: "cv", label: "CV" },
    { id: "diploma", label: "Diplôme" },
    { id: "work-certificate", label: "Certificat de travail" },
    { id: "medical-certificate", label: "Certificat médical" },
    { id: "bank-details", label: "RIB" },
    { id: "social-security", label: "Attestation sécurité sociale" },
    { id: "registration-proof", label: "Justificatif de domicile" },
    { id: "contract", label: "Contrat" },
    { id: "reference-letter", label: "Lettre de recommandation" },
    { id: "other", label: "Autre" },
  ];

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        type: "",
        name: "",
        file: null,
        deliveryDate: "",
        expirationDate: "",
        documentNumber: "",
      });
      setShowIdFields(false);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "type") {
      const isIdDocument = [
        "Carte d'identité",
        "passport",
        "residence-permit",
      ].includes(value);
      setShowIdFields(isIdDocument);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, ""),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      payload.append("type", formData.type);
      payload.append("name", formData.name);
      payload.append("file", formData.file);
      payload.append("candidateId", candidate._id);

      if (showIdFields) {
        payload.append("deliveryDate", formData.deliveryDate);
        payload.append("expirationDate", formData.expirationDate);
        payload.append("cardNumber", formData.documentNumber);
      }

      const res = await axios.post("/document", payload);
      setDocuments((prev) => [...prev, res.data]);
      onClose();
    } catch (error) {
      if (error.response?.data?.statusCode === 403) {
        localStorage.clear();
        navigate("/login");
      } else {
        console.error("Erreur lors de l'envoi du document :", error);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ajouter un document"
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type de document*
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          >
            <option value="">Sélectionnez un type de document</option>
            {documentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom du document*
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Ex: CV 2025, Diplôme Master Informatique"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          />
        </div>

        {showIdFields && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <h3 className="font-medium text-blue-800">
              Informations spécifiques pour les papiers d'identité
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Début de validité*
                </label>
                <input
                  type="date"
                  name="deliveryDate"
                  value={formData.deliveryDate}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fin de validité*
                </label>
                <input
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro du document*
              </label>
              <input
                type="text"
                name="documentNumber"
                value={formData.documentNumber}
                onChange={handleChange}
                required
                placeholder="Ex: 12AB34567"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600"
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fichier*
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                >
                  <span>Télécharger un fichier</span>
                  <input
                    id="file-upload"
                    name="file"
                    type="file"
                    className="sr-only"
                    onChange={handleFileChange}
                    required
                  />
                </label>
                <p className="pl-1">ou glisser-déposer</p>
              </div>
              <p className="text-xs text-gray-500">
                PDF, PNG, JPG, DOCX jusqu'à 10MB
              </p>
              {formData.file && (
                <p className="text-sm text-green-600 mt-2">
                  Fichier sélectionné : {formData.file.name}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Enregistrer
          </button>
        </div>
      </form>
    </Modal>
  );
};
